"use client";

import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MoreVertical, FileText } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { SnippetsAPI } from "@/lib/api";
import {
  type Snippet,
  type CreateSnippetData,
  type UpdateSnippetData,
} from "@/types/auth";
import { FillAppError } from "@/lib/FillAppError";

export default function SnippetsPage() {
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<Snippet | null>(null);
  const [deletingSnippet, setDeletingSnippet] = useState<Snippet | null>(null);
  const [formData, setFormData] = useState<CreateSnippetData>({
    name: "",
    content: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    content?: string;
  }>({});

  // Load snippets on component mount
  useEffect(() => {
    loadSnippets();
  }, []);

  const loadSnippets = async () => {
    try {
      setLoading(true);
      const data = await SnippetsAPI.fetchSnippets();
      setSnippets(data);
    } catch (error) {
      console.error("Failed to load snippets:", error);
      const errorMessage =
        error instanceof FillAppError
          ? error.message
          : "Failed to load snippets";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors: { name?: string; content?: string } = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.length > 50) {
      errors.name = "Name must be 50 characters or less";
    }

    if (!formData.content.trim()) {
      errors.content = "Content is required";
    } else if (formData.content.length > 4000) {
      errors.content = "Content must be 4000 characters or less";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddSnippet = () => {
    setEditingSnippet(null);
    setFormData({ name: "", content: "" });
    setFormErrors({});
    setShowDialog(true);
  };

  const handleEditSnippet = (snippet: Snippet) => {
    setEditingSnippet(snippet);
    setFormData({ name: snippet.name, content: snippet.content });
    setFormErrors({});
    setShowDialog(true);
  };

  const handleDeleteSnippet = (snippet: Snippet) => {
    setDeletingSnippet(snippet);
    setShowDeleteDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      if (editingSnippet) {
        // Update existing snippet
        const updates: UpdateSnippetData = {};
        if (formData.name !== editingSnippet.name)
          updates.name = formData.name.trim();
        if (formData.content !== editingSnippet.content)
          updates.content = formData.content.trim();

        if (Object.keys(updates).length > 0) {
          const updatedSnippet = await SnippetsAPI.updateSnippet(
            editingSnippet.id,
            updates
          );

          // Optimistic update
          setSnippets((prev) =>
            prev.map((snippet) =>
              snippet.id === editingSnippet.id ? updatedSnippet : snippet
            )
          );

          toast.success("Snippet updated successfully");
        }
      } else {
        // Create new snippet
        const newSnippet = await SnippetsAPI.createSnippet({
          name: formData.name.trim(),
          content: formData.content.trim(),
        });

        // Optimistic update
        setSnippets((prev) => [newSnippet, ...prev]);

        toast.success("Snippet created successfully");
      }

      setShowDialog(false);
      setFormData({ name: "", content: "" });
      setFormErrors({});
      setEditingSnippet(null);
    } catch (error) {
      console.error("Failed to save snippet:", error);
      const errorMessage =
        error instanceof FillAppError
          ? error.message
          : `Failed to ${editingSnippet ? "update" : "create"} snippet`;
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingSnippet) return;

    try {
      setDeleting(true);
      await SnippetsAPI.deleteSnippet(deletingSnippet.id);

      // Optimistic update
      setSnippets((prev) =>
        prev.filter((snippet) => snippet.id !== deletingSnippet.id)
      );

      toast.success("Snippet deleted successfully");
      setShowDeleteDialog(false);
      setDeletingSnippet(null);
    } catch (error) {
      console.error("Failed to delete snippet:", error);
      const errorMessage =
        error instanceof FillAppError
          ? error.message
          : "Failed to delete snippet";
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Card className="flex flex-col gap-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Your Snippets
          </CardTitle>
          <CardAction>
            <Button onClick={handleAddSnippet}>
              <Plus className="h-4 w-4" />
              Add Snippet
            </Button>
          </CardAction>
          <CardDescription>
            {loading
              ? "Loading..."
              : `${snippets.length} snippet${
                  snippets.length !== 1 ? "s" : ""
                } total`}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 min-h-0">
          {loading ? (
            <div className="relative overflow-hidden">
              <div className="overflow-auto max-h-content">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Name</TableHead>
                      <TableHead>Content</TableHead>
                      <TableHead className="w-[120px]">Created</TableHead>
                      <TableHead className="w-[50px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-8 w-8 rounded" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : snippets.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No snippets found</p>
              <Button onClick={handleAddSnippet} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create your first snippet
              </Button>
            </div>
          ) : (
            <div className="relative overflow-hidden">
              <div className="overflow-auto max-h-content">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Name</TableHead>
                      <TableHead>Content</TableHead>
                      <TableHead className="w-[120px]">Created</TableHead>
                      <TableHead className="w-[50px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {snippets.map((snippet) => (
                      <TableRow key={snippet.id}>
                        <TableCell className="font-medium">
                          {snippet.name}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {snippet.content}
                        </TableCell>
                        <TableCell>{formatDate(snippet.createdAt)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleEditSnippet(snippet)}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteSnippet(snippet)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingSnippet ? "Edit Snippet" : "Add New Snippet"}
              </DialogTitle>
              <DialogDescription>
                {editingSnippet
                  ? "Update your reusable text snippet"
                  : "Create a reusable text snippet for form filling and automation tasks"}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Company Bio, Contact Info"
                  maxLength={50}
                  required
                />
                {formErrors.name && (
                  <p className="text-sm text-destructive">{formErrors.name}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.name.length}/50 characters
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }))
                  }
                  placeholder="Enter your snippet content that can be reused for form filling and automation tasks."
                  maxLength={4000}
                  required
                  className="h-[300px] resize-none"
                />
                {formErrors.content && (
                  <p className="text-sm text-destructive">
                    {formErrors.content}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.content.length}/4000 characters
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? "Saving..."
                  : editingSnippet
                  ? "Update"
                  : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Snippet</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deletingSnippet?.name}
              &rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
